/**
 * Rune Stage Pack
 *
 * Creates Runescape-style rune stone meshes with physics.
 */

import { type Mesh, Vector3 } from "@babylonjs/core";
import {
  DEFAULT_RUNE_STAGE_CONFIG,
  EventBus,
  RuneManager,
  type RuneVariant,
  type StageConfigManager,
  type StagePack,
} from "@tony/nl-babylon";

/**
 * GLB configuration for rune variants.
 * Used by Three.js and model-viewer alternative viewers.
 * Each key follows the convention `{key}.glb` at the base path.
 */
export const RUNE_GLB_CONFIGS: Record<
  string,
  { glbFile?: string; glbPath?: string }
> = {
  air: {},
  astral: {},
  blood: {},
  body: {},
  chaos: {},
  cosmic: {},
  death: {},
  dust: {},
  earth: {},
  fire: {},
  lava: {},
  law: {},
  mind: {},
  mist: {},
  mud: {},
  nature: {},
  smoke: {},
  soul: {},
  steam: {},
  water: {},
  wrath: {},
};

/** Default GLB key when none specified */
export const DEFAULT_RUNE_GLB_KEY = "fire";

/**
 * Rune stage pack definition
 */
export const runeStagePack: StagePack = {
  createAssets: async ({ physicsManager, scene }) => {
    // Read config from StageConfigManager (set by StageContext) or use defaults
    const stageConfigManager = (
      scene.metadata as { stageConfigManager?: StageConfigManager } | undefined
    )?.stageConfigManager;
    const stageConfig = stageConfigManager?.config;
    const config = {
      count: stageConfig?.count ?? DEFAULT_RUNE_STAGE_CONFIG.count,
      scale: stageConfig?.scale ?? DEFAULT_RUNE_STAGE_CONFIG.scale,
      variants:
        stageConfig?.variants ??
        ([...DEFAULT_RUNE_STAGE_CONFIG.variants] as RuneVariant[]),
    };

    console.log("[RuneStagePack] Creating with config:", config);

    const eventBus = new EventBus();
    const runeManager = new RuneManager(eventBus);
    await runeManager.initialize();

    const meshes: Mesh[] = [];

    for (let i = 0; i < config.count; i++) {
      // Use configured variants, cycling if needed
      const variant = config.variants[
        i % config.variants.length
      ] as RuneVariant;
      const runeMeshes = runeManager.createRune(variant, scene, {
        scale: config.scale,
      });

      // Position runes in a circle on the ground
      const angle = (i / config.count) * Math.PI * 2;
      const radius = 2 + Math.random() * 2;
      const startX = Math.cos(angle) * radius;
      const startZ = Math.sin(angle) * radius;
      const startY = 0.5;

      // Find and configure the physics root mesh
      for (const mesh of runeMeshes) {
        if (mesh.metadata?.isPhysicsRoot) {
          mesh.position.set(startX, startY, startZ);
          mesh.rotation.y = Math.random() * Math.PI * 2;

          // Add physics - CONVEX_HULL for rune stone shape
          physicsManager.addPhysicsBody(mesh, {
            angularDamping: 0.8,
            friction: 0.6,
            linearDamping: 0.5,
            mass: 1.5, // Feels like stone
            restitution: 0.2,
            shapeType: "CONVEX_HULL",
          });

          meshes.push(mesh);
        }
      }
    }

    return meshes;
  },
  displayName: "Runes",
  id: "rune",

  interactionConfig: {
    hoverEffects: true,
    selectionEffects: true,
  },

  physicsConfig: {
    bodyOptions: {
      angularDamping: 0.8,
      friction: 0.6,
      linearDamping: 0.5,
      mass: 1.5,
      restitution: 0.2,
      shapeType: "CONVEX_HULL",
    },
    gravity: new Vector3(0, -9.8, 0),
  },
};
